import Chart from 'chart.js/auto'
import 'chartjs-adapter-moment'
import queryString from 'query-string'

class DDChart {
  canvasElement = null
  ctx = null
  chart = null
  data = null

  passColor = '#4DB27F'
  passHoverColor = '#2E6B4C'
  failColor = '#B95046'
  failHoverColor = '#6F302A'

  gridLineColor = '#F6F4FA'
  zeroLineColor = '#E2DBF0'
  fontSize = 14
  legendFontColor = '#432f6a'
  tickFontColor = '#A894D1'
  tooltipBgColor = '#160F24'
  fontFamily = '"Proxima Nova", Helvetica Neue, Helvetica, Arial, sans-serif'

  tooltipOptions = {
    mode: 'index',
    intersect: false,
    backgroundColor: this.tooltipBgColor,
    titleMarginBottom: this.fontSize,
    titleFontStyle: 'bold',
    titleFontSize: this.fontSize,
    titleFontFamily: this.fontFamily,
    bodyFontFamily: this.fontFamily,
    bodyFontSize: this.fontSize,
    bodySpacing: 10,
    footerFontSize: this.fontSize,
    footerFontStyle: 'normal',
    footerFontFamily: this.fontFamily,
    footerMarginTop: this.fontSize,
    xPadding: 10,
    yPadding: 10,
    cornerRadius: 3,
    multiKeyBackground: 'transparent',
    callbacks: {
      label: tooltipItem => {
        let label = tooltipItem.dataset.label || ''

        if (label) {
          label += ': '
        }
        label += (Math.round(tooltipItem.raw * 100) / 100).toLocaleString()

        return label
      },
      footer: tooltipItems => {
        const total = tooltipItems.reduce((accumulator, tooltipItem) => {
          return parseInt(tooltipItem.raw) + accumulator
        }, 0)
        return 'Total emails reported: ' + total.toLocaleString()
      },
    },
  }

  barChartOptions = {
    onClick: (event, chartData) => {
      this.handleClick(event, chartData)
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: this.tooltipOptions,
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        type: 'time',
        distribution: 'series',
        offset: true,
        time: {
          unit: 'day',
          tooltipFormat: 'MMM D, YYYY',
          displayFormats: {
            day: 'MMM D, YYYY',
          },
        },
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          autoSkipPadding: 80,
          maxRotation: 0,
          color: this.tickFontColor,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        color: this.tickFontColor,
        padding: 5,
        callback: value => {
          return value.toLocaleString()
        },
        grid: {
          color: this.gridLineColor,
          drawBorder: false,
          zeroLineColor: this.zeroLineColor,
        },
      },
    },
    animation: {
      duration: 0,
    },
    hover: {
      animationDuration: 0,
    },
    responsiveAnimationDuration: 0,
  }

  doughnutChartOptions = {
    onClick: (event, chartData) => {
      this.handleClick(event, chartData)
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: this.legendFontColor,
          font: {
            family: this.fontFamily,
            size: this.fontSize,
          },
        },
      },
      tooltip: this.tooltipOptions,
    },
    animation: {
      duration: 0,
    },
    hover: {
      animationDuration: 0,
    },
    responsiveAnimationDuration: 0,
  }

  constructor(element) {
    this.canvasElement = element
    this.ctx = this.canvasElement.getContext('2d')
  }

  async draw() {
    const container = this.canvasElement.parentElement

    const loader = document.createElement('div')
    loader.classList.add('chart-loader')
    loader.innerHTML = '<div class="dot-bricks"></div>'
    container.appendChild(loader)

    const chartPayload = this.canvasElement.getAttribute('data-chart-payload')

    if (chartPayload) {
      this.data = JSON.parse(chartPayload)
    } else {
      const chartDataUrl = this.canvasElement.getAttribute(
        'data-chart-payload-url'
      )
      await fetch(chartDataUrl)
        .then(response => response.json())
        .then(data => (this.data = data))
        .catch(e => console.error('Error fetching chart data:', e))
    }

    container.removeChild(loader)

    // Show an error if there's no data.
    if (!this.data) {
      const errorMsg = document.createElement('div')
      errorMsg.classList.add(
        'u-error',
        'u-text-center',
        'u-semibold',
        'u-pad-t',
        'u-pad-b'
      )
      errorMsg.innerText =
        'Oops! Something went wrong and we couldn’t load the chart.'
      container.appendChild(errorMsg)
      container.removeChild(this.canvasElement)
      document.dispatchEvent(new Event('chart:error'))
      return
    }

    if (this.data.labels.length > 1) {
      // Bar chart
      this.barChartOptions.scales.x.ticks.sampleSize = this.data.labels.length

      this.chart = new Chart(this.ctx, {
        type: 'bar',
        data: {
          labels: this.data.labels,
          datasets: [
            {
              label: 'SPF or DKIM Aligned',
              data: this.data.passed,
              backgroundColor: this.passColor,
              hoverBackgroundColor: this.passHoverColor,
              borderWidth: 0,
            },
            {
              label: 'SPF and DKIM Unaligned',
              data: this.data.failed,
              backgroundColor: this.failColor,
              hoverBackgroundColor: this.failHoverColor,
              borderWidth: 0,
            },
          ],
        },
        options: this.barChartOptions,
      })
    } else {
      // Doughnut chart
      this.tooltipOptions.callbacks = {}
      this.tooltipOptions.displayColors = false

      this.chart = new Chart(this.ctx, {
        type: 'doughnut',
        data: {
          datasets: [
            {
              data: this.data.passed.concat(this.data.failed),
              backgroundColor: [this.passColor, this.failColor],
              hoverBackgroundColor: [this.passHoverColor, this.failHoverColor],
              hoverBorderColor: ['#FFFFFF', '#FFFFFF'],
            },
          ],
          labels: ['SPF or DKIM Passed', 'SPF and DKIM Failed'],
        },
        options: this.doughnutChartOptions,
      })
    }
  }

  handleClick(event, chartData) {
    if (!chartData || chartData.length === 0) {
      return
    }

    const index = chartData[0].index
    const targetDate = this.data.labels[index]
    const params = queryString.parse(location.search)

    params.from_date = targetDate
    params.to_date = targetDate
    params.page = undefined

    window.location.search = queryString.stringify(params)
  }
}

document.addEventListener('turbolinks:load', () => {
  const charts = document.querySelectorAll('.js-chart')

  charts.forEach(chart => {
    new DDChart(chart).draw().catch(error => {
      console.error('Error drawing chart:', error)
    })
  })
})
